import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import QuestionEditor from "../components/QuestionEditor.js";
import LibraryImportModal from "../components/LibraryImportModal.js";
import {
  api,
  type AssignmentSummary,
  type AttemptRow,
  type Batch,
  type Exam,
  type ExamQuestion,
  type ExamSchedule,
} from "../api.js";

/** Faculty view: exam settings, question paper, and live attempt monitoring. */
export default function ExamBuilder() {
  const { id } = useParams<{ id: string }>();

  const [exam, setExam] = useState<Exam | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [pool, setPool] = useState<AssignmentSummary[]>([]);
  const [attempts, setAttempts] = useState<AttemptRow[]>([]);
  const [schedules, setSchedules] = useState<ExamSchedule[]>([]);
  /** Which attempt's violation detail is expanded. */
  const [openViolations, setOpenViolations] = useState<string | null>(null);
  /** Whether the library picker is open over the paper. */
  const [picking, setPicking] = useState(false);
  /** The question just added, so it opens for writing straight away. */
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addId, setAddId] = useState("");
  const [addMarks, setAddMarks] = useState(10);

  const load = () => {
    if (!id) return;
    api
      .get<{
        exam: Exam;
        questions: ExamQuestion[];
        schedules: ExamSchedule[];
        isStaff: boolean;
      }>(`/api/exams/${id}`)
      .then((r) => {
        setExam(r.exam);
        setQuestions(r.questions);
        setSchedules(r.schedules ?? []);
        return api.get<{ assignments: AssignmentSummary[] }>(`/api/courses/${r.exam.courseId}/assignments`);
      })
      .then((r) => r && setPool(r.assignments))
      .catch((e) => setError(e.message));

    api
      .get<{ attempts: AttemptRow[] }>(`/api/exams/${id}/attempts`)
      .then((r) => setAttempts(r.attempts))
      .catch(() => {});
  };

  useEffect(load, [id]);

  // Faculty watch this during an exam; a stale list is worse than useless.
  useEffect(() => {
    if (!id) return;
    const timer = setInterval(() => {
      api
        .get<{ attempts: AttemptRow[] }>(`/api/exams/${id}/attempts`)
        .then((r) => setAttempts(r.attempts))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(timer);
  }, [id]);

  function patch(fields: Partial<Exam>) {
    setExam((prev) => (prev ? { ...prev, ...fields } : prev));
  }

  async function save() {
    if (!exam || !id) return;
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      await api.patch(`/api/exams/${id}`, {
        title: exam.title,
        description: exam.description,
        opensAt: new Date(exam.opensAt).toISOString(),
        closesAt: new Date(exam.closesAt).toISOString(),
        durationMinutes: exam.durationMinutes,
        questionsToServe: exam.questionsToServe,
        shuffleQuestions: exam.shuffleQuestions,
        published: exam.published,
        requireFullscreen: exam.requireFullscreen,
        blockCopyPaste: exam.blockCopyPaste,
        maxViolations: exam.maxViolations,
        onViolationExceeded: exam.onViolationExceeded,
        captureOnFocusLoss: exam.captureOnFocusLoss,
        showResultsToStudent: exam.showResultsToStudent,
      });
      setStatus("Saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function addQuestion() {
    if (!id || !addId) return;
    setError(null);
    try {
      await api.post(`/api/exams/${id}/questions`, { assignmentId: addId, marks: addMarks, orderIndex: questions.length });
      setAddId("");
      setStatus("Question added. It is now hidden from the weekly assignment list.");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add the question.");
    }
  }

  /**
   * Write a new question straight onto the paper.
   *
   * Before this, the builder could only attach questions that already existed,
   * so setting an exam meant leaving for the course page, creating an
   * assignment, and coming back -- for every question. The new question is
   * created EXAM_ONLY so it never appears in the weekly assignment list, which
   * is what "hidden from the assignment list" has always meant for exam
   * questions attached the old way.
   */
  async function createQuestion(kind: "PROGRAMMING" | "MCQ") {
    if (!id || !exam) return;
    setError(null);
    try {
      const r = await api.post<{ assignment: { id: string } }>(
        `/api/courses/${exam.courseId}/assignments`,
        {
          title: kind === "MCQ" ? "Untitled multiple-choice question" : "Untitled question",
          kind,
          description: "",
          starterCode:
            kind === "MCQ" ? "" : "#include <stdio.h>\n\nint main(void) {\n    return 0;\n}\n",
          maxMarks: addMarks,
        }
      );
      await api.post(`/api/exams/${id}/questions`, {
        assignmentId: r.assignment.id,
        marks: addMarks,
        orderIndex: questions.length,
      });
      // Stay on the paper and open the new question for writing. Setting a
      // five-question exam used to be five round trips through the browser's
      // back button.
      setJustAdded(r.assignment.id);
      setStatus(`${kind === "MCQ" ? "Multiple-choice" : "Programming"} question added.`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create the question.");
    }
  }

  async function removeQuestion(qid: string) {
    if (!confirm("Remove this question from the exam?")) return;
    try {
      await api.del(`/api/exam-questions/${qid}`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove the question.");
    }
  }

  /**
   * Delete the exam itself. Only offered while nobody has sat it -- the server
   * refuses otherwise, since attempts and their violations are a record. The
   * questions survive: they are assignments, shared with the course.
   */
  async function deleteExam() {
    if (!id || !exam) return;
    if (
      !confirm(
        `Delete "${exam.title}"?\n\nThis cannot be undone. Its questions are not deleted — they ` +
          `remain in the course.`
      )
    )
      return;
    setError(null);
    try {
      await api.del(`/api/exams/${id}`);
      window.location.href = "/";
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the exam.");
    }
  }

  if (error && !exam) return <div className="page"><div className="error-banner">{error}</div></div>;
  if (!exam) return <div className="page muted">Loading…</div>;

  const totalMarks = questions.reduce((s, q) => s + q.marks, 0);
  const used = new Set(questions.map((q) => q.assignmentId));
  // A sheet cannot be a question; its parts can, and they are not in this list
  // because the course listing returns sheets rather than their contents.
  const available = pool.filter((a) => !used.has(a.id) && !(a.partCount ?? 0));
  const frozen = attempts.length > 0;
  const flaggedCount = attempts.filter((a) => a.violationCount > 0).length;

  return (
    <div className="page">
      <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All courses</Link>

      <div className="between" style={{ margin: "10px 0 16px" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>{exam.title}</h1>
          <div className="muted">
            Exam · {questions.length} question{questions.length === 1 ? "" : "s"} · {totalMarks} marks
          </div>
        </div>
        <div className="row">
          {!frozen && (
            <button className="danger" onClick={deleteExam}>Delete exam</button>
          )}
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {status && <div className="notice">{status}</div>}

      {frozen && (
        <div className="notice">
          {attempts.length} student(s) have started this exam. The question paper is frozen and the
          schedule can no longer be changed.
        </div>
      )}
      {exam.published && questions.length === 0 && (
        <div className="notice">This exam is published but has no questions. Students cannot start it.</div>
      )}

      <div className="card">
        <label>
          <span>Title</span>
          <input value={exam.title} onChange={(e) => patch({ title: e.target.value })} />
        </label>
        <label>
          <span>Instructions (markdown)</span>
          <textarea rows={4} value={exam.description} onChange={(e) => patch({ description: e.target.value })} />
        </label>

        <div className="grid-2">
          <label>
            <span>Opens at</span>
            <input
              type="datetime-local"
              disabled={frozen}
              value={toLocalInput(new Date(exam.opensAt))}
              onChange={(e) => patch({ opensAt: new Date(e.target.value).toISOString() })}
            />
          </label>
          <label>
            <span>Closes at</span>
            <input
              type="datetime-local"
              disabled={frozen}
              value={toLocalInput(new Date(exam.closesAt))}
              onChange={(e) => patch({ closesAt: new Date(e.target.value).toISOString() })}
            />
          </label>
        </div>

        <label>
          <span>
            Duration in minutes per student — blank means they get the whole window.
            The clock starts when a student presses Start and keeps running if they disconnect.
          </span>
          <input
            type="number"
            min={1}
            disabled={frozen}
            value={exam.durationMinutes ?? ""}
            onChange={(e) => patch({ durationMinutes: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder="90"
          />
        </label>

        <label>
          <span>
            Questions served per student — blank gives everyone the whole paper. Set it lower than
            the pool size to deal each student a random subset, which matters when batches sit at
            different times.
          </span>
          <input
            type="number"
            min={1}
            max={questions.length || undefined}
            disabled={frozen}
            value={exam.questionsToServe ?? ""}
            onChange={(e) =>
              patch({ questionsToServe: e.target.value === "" ? null : Number(e.target.value) })
            }
            placeholder={questions.length ? `all ${questions.length}` : "all"}
          />
        </label>
        {exam.questionsToServe !== null &&
          exam.questionsToServe !== undefined &&
          exam.questionsToServe > questions.length && (
            <div className="error-banner">
              This exam serves {exam.questionsToServe} questions but the pool only holds{" "}
              {questions.length}. Students will not be able to start.
            </div>
          )}

        <label className="row" style={{ marginBottom: 0, width: "auto" }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={exam.shuffleQuestions}
            onChange={(e) => patch({ shuffleQuestions: e.target.checked })}
          />
          <span style={{ marginBottom: 0 }}>Give each student the questions in a random order</span>
        </label>
        <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
          The order is fixed when a student starts, so resuming after a crash returns the same paper.
          Leave it off and everyone sits the questions in the order above.
        </p>

        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
          <div style={{ marginBottom: 10, color: "var(--muted)", fontSize: 12 }}>
            PROCTORING — a browser can notice a student leaving, not prevent it
          </div>
          <div className="row" style={{ gap: 20, flexWrap: "wrap" }}>
            <label className="row" style={{ marginBottom: 0, width: "auto" }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={exam.requireFullscreen}
                onChange={(e) => patch({ requireFullscreen: e.target.checked })}
              />
              <span style={{ marginBottom: 0 }}>Require fullscreen</span>
            </label>
            <label className="row" style={{ marginBottom: 0, width: "auto" }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={exam.blockCopyPaste}
                onChange={(e) => patch({ blockCopyPaste: e.target.checked })}
              />
              <span style={{ marginBottom: 0 }}>Discourage copy/paste</span>
            </label>
            <label className="row" style={{ marginBottom: 0, width: "auto" }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={exam.captureOnFocusLoss}
                onChange={(e) => patch({ captureOnFocusLoss: e.target.checked })}
              />
              <span style={{ marginBottom: 0 }}>Capture the screen on leaving</span>
            </label>
            <label style={{ marginBottom: 0, width: 200 }}>
              <span>Violations allowed</span>
              <input
                type="number"
                min={0}
                value={exam.maxViolations}
                onChange={(e) => patch({ maxViolations: Number(e.target.value) })}
              />
            </label>
            <label style={{ marginBottom: 0, width: 240 }}>
              <span>Once that is exceeded</span>
              <select
                value={exam.onViolationExceeded}
                onChange={(e) => patch({ onViolationExceeded: e.target.value })}
              >
                <option value="WARN">Keep warning and record it</option>
                <option value="AUTO_SUBMIT">Submit the exam automatically</option>
                <option value="LOCK">Lock the attempt</option>
              </select>
            </label>
            <label style={{ marginBottom: 0, width: 220 }}>
              <span>Show results to students</span>
              <select
                value={exam.showResultsToStudent}
                onChange={(e) => patch({ showResultsToStudent: e.target.value })}
              >
                <option value="AFTER_CLOSE">After the exam closes</option>
                <option value="IMMEDIATELY">Immediately</option>
                <option value="NEVER">Never</option>
              </select>
            </label>
          </div>

          <p className="muted" style={{ fontSize: 13, marginTop: 10, marginBottom: 0 }}>
            Switching tabs or applications, losing window focus and leaving fullscreen are each
            recorded against the attempt, with a timestamp, and appear under Exams &amp; marking.
            No browser can stop a student switching away or reading a second device — invigilation
            still needs a person in the room.
          </p>
          {exam.captureOnFocusLoss && (
            <p className="muted" style={{ fontSize: 13, marginTop: 8, marginBottom: 0 }}>
              <strong>Screen capture is on.</strong> Students are asked to share their screen before
              starting and can refuse — a refusal is recorded, not punished, and they still sit the
              exam. A picture is taken only at the moment they leave the exam window, never while
              they work. Tell your class before the exam; captures are deleted automatically after
              the retention period.
            </p>
          )}
        </div>

        <label className="row" style={{ marginTop: 14, marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={exam.published}
            onChange={(e) => patch({ published: e.target.checked })}
          />
          <span style={{ marginBottom: 0 }}>Published (visible to students)</span>
        </label>
      </div>

      <BatchWindows
        examId={exam.id}
        courseId={exam.courseId}
        schedules={schedules}
        durationMinutes={exam.durationMinutes}
        onChanged={load}
        onError={setError}
        onStatus={setStatus}
      />

      <div className="card">
        <div className="between" style={{ marginBottom: 12 }}>
          <strong>Question paper ({questions.length})</strong>
          <span className="muted">{totalMarks} marks total</span>
        </div>

        {questions.map((q, i) => (
          <QuestionEditor
            key={q.id}
            questionId={q.assignment.id}
            index={i + 1}
            defaultOpen={q.assignment.id === justAdded}
            onSummaryChange={load}
            actions={
              !frozen ? (
                <button className="danger" onClick={() => removeQuestion(q.id)}>Remove</button>
              ) : null
            }
          />
        ))}

        {questions.length === 0 && <div className="muted">No questions yet.</div>}

        {!frozen && (
          <>
          <div className="add-question" style={{ marginTop: 14 }}>
            <strong style={{ fontSize: 18, lineHeight: 1 }}>+</strong>
            <span className="muted" style={{ fontSize: 13 }}>Add a question</span>
            <button onClick={() => createQuestion("PROGRAMMING")}>Programming</button>
            <button onClick={() => createQuestion("MCQ")}>Multiple choice</button>
            {/* A library question arrives written, with its tests and marks;
                the other two are blanks to fill in here. */}
            <button onClick={() => setPicking(true)}>From library</button>
            <span className="muted" style={{ fontSize: 13 }}>
              on this paper only
            </span>
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <span className="muted" style={{ fontSize: 12 }}>OR REUSE</span>
            <select value={addId} onChange={(e) => setAddId(e.target.value)} style={{ flex: 1 }}>
              <option value="">Add a question from this course…</option>
              {available.map((a) => (
                <option key={a.id} value={a.id}>{a.title}</option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              style={{ width: 90 }}
              value={addMarks}
              onChange={(e) => setAddMarks(Number(e.target.value))}
              title="Marks"
            />
            <button onClick={addQuestion} disabled={!addId}>Add</button>
          </div>
          </>
        )}

        {picking && exam && (
          <LibraryImportModal
            into={{ examId: exam.id }}
            title={exam.title}
            importLabel="Add to paper"
            onClose={() => setPicking(false)}
            onImported={(summary) => {
              setStatus(`${summary} Hidden from the weekly assignment list.`);
              load();
            }}
          />
        )}
      </div>

      <div className="card">
        <div className="between" style={{ marginBottom: 12 }}>
          <strong>Attempts ({attempts.length})</strong>
          <div className="row">
            {flaggedCount > 0 && (
              <span className="badge fail">
                {flaggedCount} attempt{flaggedCount === 1 ? "" : "s"} flagged
              </span>
            )}
            <span className="muted" style={{ fontSize: 12 }}>refreshes every 15s</span>
            {/* Every enrolled student, not only those listed above -- a student
                who never started belongs in the marks sheet as a zero. */}
            <a href={`/api/exams/${id}/marks.csv`} download>
              <button>Export marks (CSV)</button>
            </a>
          </div>
        </div>
        {attempts.length === 0 ? (
          <div className="muted">Nobody has started this exam.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Student</th><th>Roll no.</th><th>Started</th><th>Deadline</th>
                <th>Answered</th><th>Status</th><th>Violations</th><th>Score</th><th>IP</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((a) => (
                <>
                <tr key={a.id}>
                  <td>{a.student.name}</td>
                  <td className="muted">{a.student.rollNumber ?? "—"}</td>
                  <td className="muted">{new Date(a.startedAt).toLocaleTimeString()}</td>
                  <td className="muted">{new Date(a.deadlineAt).toLocaleTimeString()}</td>
                  <td>{a.answered} / {questions.length}</td>
                  <td>
                    <span className={`badge ${a.status === "IN_PROGRESS" ? "warn" : "pass"}`}>
                      {a.status.toLowerCase().replace(/_/g, " ")}
                    </span>
                  </td>
                  <td>
                    {a.violationCount === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      <button
                        className="link-button"
                        onClick={() => setOpenViolations(openViolations === a.id ? null : a.id)}
                        title="Show what happened and when"
                      >
                        <span className={`badge ${a.violationCount > exam.maxViolations ? "fail" : "warn"}`}>
                          {a.violationCount} of {exam.maxViolations}
                        </span>
                      </button>
                    )}
                  </td>
                  <td>{a.finalScore ?? "—"}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{a.ipAtStart || "—"}</td>
                </tr>
                {openViolations === a.id && (
                  <tr key={`${a.id}-v`} className="detail-row">
                    <td colSpan={9}>
                      <div className="detail-body">
                        <h3>WHAT HAPPENED</h3>
                        {exam.captureOnFocusLoss && (
                          <p style={{ fontSize: 13, marginTop: 0 }}>
                            Screen sharing:{" "}
                            <span className={`badge ${shareBadge(a.screenShareState)}`}>
                              {shareLabel(a.screenShareState)}
                            </span>
                          </p>
                        )}
                        {a.violations.length === 0 ? (
                          <div className="muted">
                            Counted before the detail was recorded, so only the total is known.
                          </div>
                        ) : (
                          <ol style={{ margin: 0, paddingLeft: 20 }}>
                            {a.violations.map((v, i) => (
                              <li key={i} className="muted" style={{ fontSize: 13 }}>
                                {violationLabel(v.kind)} — {new Date(v.at).toLocaleTimeString()}
                              </li>
                            ))}
                          </ol>
                        )}
                        {exam.captureOnFocusLoss && <AttemptShots attemptId={a.id} />}

                        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                          A browser can notice a student leaving, not prevent it. Treat this as a
                          prompt to look, not proof on its own.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * Per-batch exam windows.
 *
 * A lab that cannot seat everyone at once sits the exam in batches, and the
 * server has always honoured a batch's own window when an attempt starts --
 * there was simply no way to set one. A batch without an override uses the
 * exam's window, which is the common case and stays a single blank row.
 */
function BatchWindows({
  examId,
  courseId,
  schedules,
  durationMinutes,
  onChanged,
  onError,
  onStatus,
}: {
  examId: string;
  courseId: string;
  schedules: ExamSchedule[];
  durationMinutes: number | null;
  onChanged: () => void;
  onError: (msg: string) => void;
  onStatus: (msg: string) => void;
}) {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [draft, setDraft] = useState<Record<string, { opensAt: string; closesAt: string }>>({});
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ batches: Batch[] }>(`/api/courses/${courseId}/batches`)
      .then((r) => setBatches(r.batches))
      .catch(() => {});
  }, [courseId]);

  useEffect(() => {
    const next: Record<string, { opensAt: string; closesAt: string }> = {};
    for (const s of schedules) {
      next[s.batchId] = {
        opensAt: toLocalInput(new Date(s.opensAt)),
        closesAt: toLocalInput(new Date(s.closesAt)),
      };
    }
    setDraft(next);
  }, [schedules]);

  if (batches.length === 0) return null;

  async function save(batchId: string) {
    const value = draft[batchId];
    if (!value?.opensAt || !value?.closesAt) {
      onError("Give both an opening and a closing time for that batch.");
      return;
    }
    setBusy(batchId);
    try {
      await api.put(`/api/exams/${examId}/schedules/${batchId}`, {
        opensAt: new Date(value.opensAt).toISOString(),
        closesAt: new Date(value.closesAt).toISOString(),
      });
      onStatus("Batch window saved.");
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not save the batch window.");
    } finally {
      setBusy(null);
    }
  }

  async function clear(batchId: string) {
    setBusy(batchId);
    try {
      await api.del(`/api/exams/${examId}/schedules/${batchId}`);
      onStatus("Batch window removed; it uses the exam's own window again.");
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not remove the batch window.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card">
      <div style={{ marginBottom: 4, color: "var(--muted)", fontSize: 12 }}>BATCH WINDOWS</div>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Leave a batch blank to use the exam's own window. Set one when batches sit at different
        times — and consider serving a subset of questions, or the earlier batch hands the paper
        to the later one. Changing a window does not move an attempt already in progress.
        {durationMinutes ? ` Each window must be at least ${durationMinutes} minutes.` : ""}
      </p>

      <table>
        <thead>
          <tr><th>Batch</th><th>Opens</th><th>Closes</th><th /></tr>
        </thead>
        <tbody>
          {batches.map((b) => {
            const has = schedules.some((s) => s.batchId === b.id);
            const value = draft[b.id] ?? { opensAt: "", closesAt: "" };
            return (
              <tr key={b.id}>
                <td>
                  <strong>{b.name}</strong>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {b.studentCount} student{b.studentCount === 1 ? "" : "s"}
                    {has ? "" : " · uses the exam window"}
                  </div>
                </td>
                <td>
                  <input
                    type="datetime-local"
                    value={value.opensAt}
                    onChange={(e) =>
                      setDraft({ ...draft, [b.id]: { ...value, opensAt: e.target.value } })
                    }
                  />
                </td>
                <td>
                  <input
                    type="datetime-local"
                    value={value.closesAt}
                    onChange={(e) =>
                      setDraft({ ...draft, [b.id]: { ...value, closesAt: e.target.value } })
                    }
                  />
                </td>
                <td>
                  <div className="row">
                    <button disabled={busy === b.id} onClick={() => save(b.id)}>
                      {busy === b.id ? "…" : has ? "Update" : "Set"}
                    </button>
                    {has && (
                      <button className="danger" disabled={busy === b.id} onClick={() => clear(b.id)}>
                        Clear
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The captures for one attempt, fetched only when a teacher opens the detail --
 * images of a student's screen are not something to load speculatively.
 */
function AttemptShots({ attemptId }: { attemptId: string }) {
  const [shots, setShots] = useState<
    { id: string; reason: string; createdAt: string; width: number; height: number }[]
  >([]);
  const [retention, setRetention] = useState<number | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ shots: typeof shots; retentionDays: number }>(`/api/attempts/${attemptId}/shots`)
      .then((r) => {
        setShots(r.shots);
        setRetention(r.retentionDays);
      })
      .catch(() => {});
  }, [attemptId]);

  if (shots.length === 0) return null;

  return (
    <section style={{ marginTop: 16 }}>
      <h3>SCREEN AT THE MOMENT OF LEAVING ({shots.length})</h3>
      <div className="shot-grid">
        {shots.map((s) => (
          <figure key={s.id} className="shot">
            <img
              src={`/api/shots/${s.id}`}
              alt={`Screen at ${new Date(s.createdAt).toLocaleTimeString()}`}
              onClick={() => setOpen(open === s.id ? null : s.id)}
            />
            <figcaption className="muted">
              {new Date(s.createdAt).toLocaleTimeString()} · {violationLabel(s.reason === "FOCUS_LOSS" ? "BLUR" : s.reason)}
            </figcaption>
          </figure>
        ))}
      </div>
      {open && (
        <div className="shot-full" onClick={() => setOpen(null)}>
          <img src={`/api/shots/${open}`} alt="Captured screen, full size" />
        </div>
      )}
      {retention !== null && (
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
          Deleted automatically {retention} days after capture. Opening one is recorded in the
          audit log.
        </p>
      )}
    </section>
  );
}

function shareLabel(state?: string): string {
  switch (state) {
    case "GRANTED": return "whole screen shared";
    case "PARTIAL": return "only part of the screen shared";
    case "DECLINED": return "declined";
    case "STOPPED": return "stopped by the student";
    case "UNSUPPORTED": return "browser cannot share";
    default: return "not asked";
  }
}

function shareBadge(state?: string): string {
  if (state === "GRANTED") return "pass";
  if (state === "PARTIAL") return "warn";
  if (state === "NOT_ASKED" || !state) return "draft";
  return "fail";
}

/** Plain words for a violation kind, for a reader who is not a programmer. */
function violationLabel(kind: string): string {
  switch (kind) {
    case "TAB_SWITCH": return "Switched away from the exam tab";
    case "BLUR": return "Exam window lost focus";
    case "FULLSCREEN_EXIT": return "Left fullscreen";
    case "COPY": return "Tried to copy";
    case "PASTE": return "Tried to paste";
    default: return kind.toLowerCase().replace(/_/g, " ");
  }
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
